import React from "react";
import { useLiveAnnouncement } from "../shared/hooks/use-live-announcement";
import { useOverflowList } from "../shared/hooks/use-overflow-list";
import { LanguagePill } from "./components/LanguagePill";
import { MeetingStateBadge } from "./components/MeetingStateBadge";
import { PhoneLanguageDisclosure } from "./components/PhoneLanguageDisclosure";
import {
  ArrowRightIcon,
  GlobeIcon,
  HeadphonesIcon,
  TranscriptIcon
} from "./components/Icons";
import type { LanguageAccessModel, LanguageOption } from "./types";

interface LanguageAccessPanelProps {
  readonly model: LanguageAccessModel;
}

const MAX_VISIBLE_PILLS = 4;

export function LanguageAccessPanel({
  model
}: LanguageAccessPanelProps): React.JSX.Element | null {
  const { visible, overflowCount, isExpanded, toggleProps, listId } =
    useOverflowList<LanguageOption>(model.languages, MAX_VISIBLE_PILLS);

  const announcement = useLiveAnnouncement(
    isExpanded
      ? `Showing all ${model.languages.length} languages`
      : `Showing ${MAX_VISIBLE_PILLS} of ${model.languages.length} languages`,
    String(isExpanded)
  );

  // Matches the server gate: the panel exists to offer languages, so without any there is
  // nothing to show. LanguageAccessModelBuilder already returns null in that case.
  if (model.languages.length === 0) {
    return null;
  }

  return (
    <div className="pi-langaccess">
      <section
        className={`invite la-panel ${model.state}`}
        aria-labelledby="pi-la-h"
      >
        <div className="invite-top">
          <span className="globe">
            <GlobeIcon />
          </span>
          <div className="invite-titles">
            <h2 id="pi-la-h" className="la-panel__title">
              Read this meeting’s transcript in your language
            </h2>
          </div>
          <MeetingStateBadge state={model.state} />
        </div>

        {/*
          The mode tags stay readable by assistive technology. The prototype marked them
          aria-hidden, which silently drops "this meeting has interpreted audio" for exactly the
          people most likely to need it.
        */}
        <p className="invite-sub">
          <span className="modes">
            {model.hasCaptions && (
              <span className="modetag">
                <TranscriptIcon />
                Translated transcript
              </span>
            )}
            {model.hasInterpretedAudio && (
              <span className="modetag">
                <HeadphonesIcon />
                Hear interpreted audio
              </span>
            )}
          </span>
          The full record of what was said is available to read in your language.
        </p>

        <div
          className="langs"
          id={listId}
          role="list"
          aria-label="Available languages"
        >
            {visible.map((language) => (
              <LanguagePill
                key={language.shortCode}
                language={language}
                transcriptUrl={model.transcriptUrl}
              />
            ))}
            {overflowCount > 0 && (
              /* role="list" admits only listitem children, and the toggle is a control rather
                 than a language -- so it travels inside one instead of pretending to be one. */
              <span role="listitem">
                <button className="langpill more la-pill--more" {...toggleProps}>
                  {isExpanded
                    ? "Show fewer languages"
                    : `+ ${overflowCount} more`}
                </button>
              </span>
          )}
        </div>

        <div className="invite-actions">
          <a
            className="btn-open"
            href={model.transcriptUrl}
            target="_blank"
            rel="noopener"
            data-testid="open-live-captions"
          >
            Read the transcript
            <ArrowRightIcon />
            <span className="visually-hidden"> (opens in a new tab)</span>
          </a>

          {model.dialInNumber && model.state !== "pending" && (
            <PhoneLanguageDisclosure
              dialInNumber={model.dialInNumber}
              languages={model.languages}
            />
          )}
        </div>

        <div className="visually-hidden" role="status" aria-live="polite">
          {announcement}
        </div>
      </section>
    </div>
  );
}
